<script setup lang="ts">
  defineOptions({
    name: 'CPRootContainer',
  })

  import { computed, defineAsyncComponent, ref, Teleport } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { useAuth } from '@common/auth-layer/composables/useAuth'

  import { HIGHRISE_NOTIFICATION_CONFIG } from '@common/core-layer/constants'

  import { useLocation } from '@common/location-layer/composables/useLocation'
  import { usePortalTheme } from '@common/location-layer/composables/usePortalTheme'

  import { useBrandedHead } from '@clientportal/routing-layer/composables/useBrandedHead'

  import { createElementId } from '@core/utils/core.utils'

  import { resolveCpBreadcrumbRegistryKey } from '@common/navigation-layer/config/cp-breadcrumb-registry'

  import { useCPBreadcrumbs } from '@common/navigation-layer/composables/useCPBreadcrumbs'

  import { useSidebarDrawer } from '@common/shared-ui-layer/composables/useSidebarDrawer'

  import CustomHamburgerMenuIcon from '@common/shared-ui-layer/icons/CustomHamburgerMenuIcon.vue'

  import TopBar from '@common/shared-ui-layer/components/TopBar.vue'

  import TopBarMobile from '@common/shared-ui-layer/components/TopBarMobile.vue'

  import { ChevronLeftIcon, ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { HLContentWrap } from '@platform-ui/highrise'

  import CPBreadcrumb from '@ui/components/CPBreadcrumb.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPText from '@ui/components/CPText.vue'

  import CPTooltip from '@ui/components/CPTooltip.vue'

  import { AccountSettingsTabKey } from '@layers/account-settings/common-layer/utils/account-settings-config'

  import { useAccountSettingsTabVisibility } from '@layers/account-settings/common-layer/composables/useAccountSettingsTabVisibility'

  import BetaPreviewBanner from '@common/beta-preview-layer/components/BetaPreviewBanner.vue'
  import CoursesAdminPreviewBanner from '@courses/admin-preview-layer/components/CoursesAdminPreviewBanner.vue'

  import { useCoursesAdminPreview } from '@courses/admin-preview-layer/composables/useCoursesAdminPreview'

  import { useCPSupportChatWidget } from '@clientportal/support-chat-layer/composables/useCPSupportChatWidget'
  import { useGroupCustomCode } from '@communities/groups-layer/composables/useGroupCustomCode'

  import { useMyCoursesSearch } from '@clientportal/cp-courses-layer/composables/useMyCoursesSearch'
  import { useEventsDirectorySearch } from '@clientportal/cp-events-layer/composables/useEventsDirectorySearch'
  import { useEventsDetailBreadcrumbLabel } from '@clientportal/cp-events-layer/composables/useEventsDetailBreadcrumbLabel'
  import { useCPCommunitiesGroupBreadcrumbContext } from '../composables/useCPCommunitiesGroupBreadcrumb'

  import { useCPCoursesProductBreadcrumbContext } from '../composables/useCPCoursesProductBreadcrumb'

  import { useCPRouteContext } from '../composables/useCPRouteContext'

  import { useClientPortalAuthStateWatcher } from '../composables/useClientPortalAuthStateWatcher'

  import { useCPCustomCode } from '../composables/useCPCustomCode'

  import { useCPChatShell } from '../composables/useCPChatShell'

  import { useCPRootVisibility } from '../composables/useCPRootVisibility'

  import { useActiveSearchContext } from '../composables/useActiveSearchContext'

  import { useCPSwitcherSidebar } from '../composables/useCPSwitcherSidebar'

  import { usePortalSidebar } from '../composables/usePortalSidebar'

  import { useCPJoinedGroupsListPrefetch } from '../composables/useCPJoinedGroupsListPrefetch'

  import CPPortalSidebar from './CPPortalSidebar.vue'

  import CPPortalSidebarMobileDrawer from './CPPortalSidebarMobileDrawer.vue'

  const LoginModal = defineAsyncComponent(() => import('@clientportal/auth-layer/components/LoginModal.vue'))

  const ChatContainer = defineAsyncComponent(() => import('@communities/chats-layer/components/organisms/ChatContainer.vue'))

  const SearchWidget = defineAsyncComponent(() => import('@common/search-layer/components/SearchWidget.vue'))

  const MyCoursesNavbarSearch = defineAsyncComponent(() => import('@clientportal/cp-courses-layer/components/MyCoursesNavbarSearch.vue'))
  const EventsDirectoryNavbarSearch = defineAsyncComponent(
    () => import('@clientportal/cp-events-layer/components/molecules/EventsDirectoryNavbarSearch.vue')
  )

  const { searchQuery: myCoursesSearchQuery, setSearchQuery: setMyCoursesSearchQuery, reset: resetMyCoursesSearch } = useMyCoursesSearch()
  const {
    searchQuery: eventsDirectorySearchQuery,
    setSearchQuery: setEventsDirectorySearchQuery,
    reset: resetEventsDirectorySearch,
  } = useEventsDirectorySearch()

  const { t } = useI18n()

  const betaPreviewBannerMessage = computed(() => t('clientportal.betaPreview.bannerMessage'))

  const coursesAdminPreviewBannerMessage = computed(() => t('clientportal.adminPreview.bannerMessage'))

  const authStore = useAuth().store

  const { toggle: toggleSidebarDrawer } = useSidebarDrawer()

  // Freeze the chrome (sidebar + top bar) in admin-preview builder mode; the previewed theme itself stays interactive.
  const { isActive: isAdminPreviewActive, builderMode: isAdminPreviewBuilderMode } = useCoursesAdminPreview()
  const isAdminPreviewBuilder = computed(() => Boolean(isAdminPreviewActive.value && isAdminPreviewBuilderMode.value))

  const {
    route,
    isMobile,
    isAuthPage,
    isAccountSettingsPage,
    isNonCPNavigationRoute,
    isCommunityLayerPage,
    currentGroupSlug,
    selectedProductId,
  } = useCPRouteContext()

  const visibility = useCPRootVisibility(isNonCPNavigationRoute, isMobile, isAuthPage, isAccountSettingsPage)

  const { showPortalSidebar, showSidebarToggleBar, showPortalMobileDrawer, showTopBar, showMobileTopBar } = visibility

  const { portalName, portalIcon, portalSubtitle } = useCPSwitcherSidebar(currentGroupSlug, selectedProductId)

  const { isCollapsed, toggleSidebar, navigationGroups, navigationGroupsMobile, selectedItem, currentPageTitle } = usePortalSidebar()

  // Pass only the route fields the chat shell needs, and coerce the route name
  // into a stable string|null shape so its visibility and navigation checks stay predictable.
  const chatShellRoute = {
    get path() {
      return route.path
    },
    get name() {
      return typeof route.name === 'string' ? route.name : null
    },
  }

  const chatContainerRef = ref<{ handleChatClick: () => Promise<void> } | null>(null)

  const { chatUnreadCount, handleTopBarChatClick, showChatButton, shouldMountChatContainer } = useCPChatShell(
    chatShellRoute,
    chatContainerRef
  )

  const { locationId } = useLocation()

  const { searchContext } = useActiveSearchContext()

  const isCommunitiesGroupRoute = computed(() => route.name?.toString().startsWith('communities-groups-') ?? false)
  const isCoursesRoute = computed(() => (isNonCPNavigationRoute.value && route.path?.toString().startsWith('/courses/')) ?? false)

  /** My Courses page only — drives the in-place course search in the top bar center slot. */
  const isMyCoursesRoute = computed(() => resolveCpBreadcrumbRegistryKey(route) === 'clientportal-courses-my-courses')
  const breadcrumbRegistryKey = computed(() => resolveCpBreadcrumbRegistryKey(route))
  const isEventsDirectoryRoute = computed(
    () =>
      breadcrumbRegistryKey.value === 'clientportal-events-upcoming' ||
      breadcrumbRegistryKey.value === 'clientportal-events-past' ||
      breadcrumbRegistryKey.value === 'clientportal-events-live'
  )
  const isShellSearchRoute = computed(() => isMyCoursesRoute.value || isEventsDirectoryRoute.value)

  /**
   * Mobile top-bar search wiring. My Courses uses its own UI store (filters the grid in place);
   * every other route defers to the active search context (courses search, communities, etc.).
   */
  const mobileShowSearch = computed(() =>
    isShellSearchRoute.value ? authStore.isLoggedIn : (searchContext.value?.shouldShowSearch?.value ?? false)
  )

  const mobileInitialSearchQuery = computed(() => {
    if (isMyCoursesRoute.value) return myCoursesSearchQuery.value
    if (isEventsDirectoryRoute.value) return eventsDirectorySearchQuery.value
    return searchContext.value?.initialQuery?.value ?? ''
  })

  const handleMobileSearchOpen = () => {
    if (isShellSearchRoute.value) return
    searchContext.value?.onSearchOpen?.()
  }

  const handleMobileSearchInputChange = (value: string) => {
    if (isMyCoursesRoute.value) {
      setMyCoursesSearchQuery(value)
      return
    }
    if (isEventsDirectoryRoute.value) {
      setEventsDirectorySearchQuery(value)
      return
    }
    searchContext.value?.onInputChange?.(value)
  }

  const handleMobileSearchSubmit = (query: string) => {
    if (isMyCoursesRoute.value) {
      setMyCoursesSearchQuery(query)
      return
    }
    if (isEventsDirectoryRoute.value) {
      setEventsDirectorySearchQuery(query)
      return
    }
    searchContext.value?.onSubmit(query)
  }

  const handleMobileSearchClose = () => {
    if (isMyCoursesRoute.value) {
      resetMyCoursesSearch()
      return
    }
    if (isEventsDirectoryRoute.value) {
      resetEventsDirectorySearch()
      return
    }
    searchContext.value?.onSearchClose?.()
  }

  /** Resolved MongoDB _id for the active group; preferred over slug for SearchWidget cache keying. */
  const currentGroupId = computed(() => communitiesGroupBreadcrumb.value?.groupId)

  const effectiveSidebarCollapsed = computed(() => isNonCPNavigationRoute.value || isAccountSettingsPage.value || isCollapsed.value)

  const onSignup = () => {
    authStore.openLoginModal('SIGN_UP')
  }

  /**
   * Mobile top-bar center text.
   * - Account settings → portal name (branding context)
   * - Community/course routes → last breadcrumb value (group name or course name)
   * - CP shell routes → current page title
   */
  const topBarTitle = computed(() => {
    if (isAccountSettingsPage.value) return portalName.value
    if (isNonCPNavigationRoute.value) {
      const groupName = communitiesGroupBreadcrumb.value?.groupName
      if (groupName) return groupName
      const courseName = coursesProductBreadcrumb.value?.courseName
      if (courseName) return courseName
      return portalName.value
    }
    return currentPageTitle.value
  })

  const { validVisibleTabIds } = useAccountSettingsTabVisibility()

  /**
   * Shell pages set `meta.cpRoute`; communities/courses layer pages often do not, but we still want
   * breadcrumbs when resolveCpBreadcrumbRegistryKey matches (hybrid registry + path rules).
   * Gated on desktop TopBar only — CPBreadcrumb is not rendered in TopBarMobile, so enabling on
   * mobile would run queries (e.g. course title) with no visible trail.
   */
  const breadcrumbsEnabled = computed(() => {
    if (isAuthPage.value) return false
    if (!showTopBar.value) return false
    if (!isNonCPNavigationRoute.value) return true
    return resolveCpBreadcrumbRegistryKey(route) !== null
  })

  /**
   * Account settings last breadcrumb segment is driven by `?activeTab=<tab>`.
   * Tab key → i18n key mirrors AccountSettingsSwitcherRail (`account.web.sidebar.<tab_underscored>`).
   * Unknown query values fall back to profile to stay in sync with AccountSettingsSwitcherRail
   * and AccountSettingsContent, which apply the same fallback before rendering.
   */
  const accountSettingsTabLabel = computed(() => {
    if (!isAccountSettingsPage.value) return undefined
    // [].flat()[0] normalizes string | string[] | undefined → first string value (Vue Router repeats keys as string[])
    const raw = [route.query.activeTab].flat()[0] ?? ''
    const tab = validVisibleTabIds.value.includes(raw as AccountSettingsTabKey) ? raw : AccountSettingsTabKey.Profile
    return t(`account.web.sidebar.${tab.replace(/-/g, '_')}`)
  })

  const communitiesGroupBreadcrumb = useCPCommunitiesGroupBreadcrumbContext()

  const coursesProductBreadcrumb = useCPCoursesProductBreadcrumbContext()
  const eventsDetailBreadcrumbLabel = useEventsDetailBreadcrumbLabel()
  const lastSegmentLabel = computed(() => accountSettingsTabLabel.value ?? eventsDetailBreadcrumbLabel.value)

  const { breadcrumbs, breadcrumbId, separator } = useCPBreadcrumbs({
    enabled: breadcrumbsEnabled,
    communitiesGroup: communitiesGroupBreadcrumb,
    coursesProduct: coursesProductBreadcrumb,
    lastSegmentLabel,
  })

  const showBreadcrumbTrail = computed(() => Boolean(breadcrumbs.value?.length))

  /** Mobile account settings with `?activeTab=` — section sub-header only; hide portal TopBarMobile. */
  const isAccountSettingsMobileSection = computed(() => {
    if (!isAccountSettingsPage.value || !isMobile.value) {
      return false
    }
    const activeTab = [route.query.activeTab].flat()[0]
    return typeof activeTab === 'string' && activeTab.length > 0
  })

  const showShellMobileTopBar = computed(() => showMobileTopBar.value && !isAccountSettingsMobileSection.value)

  // Owns SSR-safe favicon <link> and `{Portal} | {Page}` titleTemplate. Flips
  // store.brandedHeadOwnsDocument so applyBranding's legacy imperative writes
  // become no-ops on clientportal — Vue head is the single writer here.
  useBrandedHead()

  usePortalTheme()

  useClientPortalAuthStateWatcher()

  // Inject portal-level custom CSS / JS / tracking code when liveMode is enabled
  useCPCustomCode()
  // Inject communities group-level custom CSS / JS / tracking code on group routes.
  useGroupCustomCode()

  /** Third-party support chat (LiveChat) from portal `chatWidgetSettings`; client-only. */
  useCPSupportChatWidget()

  useCPJoinedGroupsListPrefetch(isAuthPage)
</script>

<template>
  <HLContentWrap :full-screen="true" namespace="membershipApp" :notification-config="HIGHRISE_NOTIFICATION_CONFIG">
    <div class="cp-root-shell">
      <BetaPreviewBanner :message="betaPreviewBannerMessage" />
      <CoursesAdminPreviewBanner :message="coursesAdminPreviewBannerMessage" />
      <!-- Anchor element for communities group-level custom header tracking code injection -->
      <div id="communities-siteCustomHeader" />
      <!-- Anchor element for portal-level custom header tracking code injection -->
      <div id="cp-siteCustomHeader" />
      <div
        id="cp-root-container"
        class="cp-root-container"
        :class="{
          'sidebar-collapsed': effectiveSidebarCollapsed,
          'auth-page': isAuthPage,
          'account-settings-page': isAccountSettingsPage,
          'switcher-sidebar-mode': isCommunityLayerPage,
          'account-settings-mobile-section': isAccountSettingsMobileSection,
        }"
      >
        <CPPortalSidebar
          :portal-icon="portalIcon"
          :portal-name="portalName"
          :portal-subtitle="portalSubtitle"
          :navigation-groups="navigationGroups"
          :selected-item="selectedItem"
          :is-collapsed="effectiveSidebarCollapsed"
          :is-visible="showPortalSidebar"
          @toggle="toggleSidebar"
        />

        <!-- Content area -->
        <div class="cp-content-area" :class="{ 'cp-content-area--mobile-bg': isMobile }">
          <CPTooltip v-if="showSidebarToggleBar" placement="right" variant="dark">
            <template #trigger>
              <div
                :id="createElementId('sidebar', 'toggle', 'button')"
                class="sidebar-toggle-btn cursor-pointer"
                :class="{ 'sidebar-collapsed': isCollapsed }"
                role="button"
                tabindex="0"
                :aria-label="isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar')"
                :title="isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar')"
                @click="toggleSidebar"
                @keydown.enter="toggleSidebar"
                @keydown.space.prevent="toggleSidebar"
              >
                <span class="sidebar-toggle-inner">
                  <span class="toggle-line"></span>
                  <span class="sidebar-toggle-icons">
                    <ChevronLeftIcon v-if="!isCollapsed" class="sidebar-toggle-icon" />
                    <ChevronRightIcon v-else class="sidebar-toggle-icon" />
                  </span>
                </span>
              </div>
            </template>
            <div class="px-2 py-1 text-sm">
              {{ isCollapsed ? t('clientportal.sidebar.expandSidebar') : t('clientportal.sidebar.collapseSidebar') }}
            </div>
          </CPTooltip>

          <TopBar
            v-if="showTopBar"
            :class="['cp-topbar', isAdminPreviewBuilder ? 'pointer-events-none select-none' : '']"
            :aria-disabled="isAdminPreviewBuilder"
            :tabindex="isAdminPreviewBuilder ? -1 : undefined"
            :show-bottom-border="true"
            :compact-left-section="true"
            :show-chat-button="showChatButton"
            :chat-unread-count="chatUnreadCount"
            :show-profile-card="!showPortalSidebar"
            :wide-center-section="authStore.isLoggedIn && isShellSearchRoute"
            @loginClick="authStore.openLoginModal()"
            @signupClick="onSignup"
            @chatClick="handleTopBarChatClick"
          >
            <template #page-title>
              <CPBreadcrumb
                v-if="showBreadcrumbTrail && breadcrumbs"
                :id="breadcrumbId"
                :breadcrumbs="breadcrumbs"
                :home="false"
                :separator="separator"
              />
              <CPText v-else-if="topBarTitle" size="lg" weight="semibold" class="text-body">
                {{ topBarTitle }}
              </CPText>
            </template>
            <template #announcements>
              <AnnouncementsTopBarButton
                :button-id="createElementId('topbar', 'desktop', 'announcements-button')"
                :badge-id="createElementId('topbar', 'desktop', 'announcements-unread-badge')"
                button-class="flex-shrink-0 !h-9 !w-9 !min-w-0 !rounded-lg !p-0 transition-colors duration-200 hover:!bg-hover"
              />
            </template>
            <!-- Desktop: course search on My Courses; communities search on group routes. Mobile TopBar uses useActiveSearchContext (courses, my-library, etc.) — intentional product split, not an oversight. -->
            <template #search>
              <ClientOnly>
                <MyCoursesNavbarSearch v-if="authStore.isLoggedIn && isMyCoursesRoute" class="w-full" />
                <EventsDirectoryNavbarSearch v-else-if="authStore.isLoggedIn && isEventsDirectoryRoute" class="w-full" />
                <SearchWidget
                  v-else-if="authStore.isLoggedIn && locationId && isCommunitiesGroupRoute && isNonCPNavigationRoute"
                  :location-id="locationId"
                  :group-id="currentGroupId"
                  :placeholder="t('clientportal.search.placeholder')"
                  size="sm"
                  :use-query-params="true"
                />
              </ClientOnly>
            </template>
          </TopBar>

          <header
            v-else-if="showShellMobileTopBar"
            id="cp-mobile-topbar"
            :class="['cp-topbar', isAdminPreviewBuilder ? 'pointer-events-none select-none' : '']"
            :aria-disabled="isAdminPreviewBuilder"
            :tabindex="isAdminPreviewBuilder ? -1 : undefined"
          >
            <!-- Account settings: dedicated bar (portal + home + profile); no menu hamburger / search. -->
            <TopBarMobile
              v-if="isAccountSettingsPage"
              class="h-full w-full"
              :show-bottom-border="true"
              :show-logo="Boolean(portalIcon)"
              :logo-src="portalIcon || ''"
              logo-shape="square"
              :show-search="false"
              @loginClick="authStore.openLoginModal()"
              @signupClick="onSignup"
            >
              <template #page-title>
                <CPBreadcrumb
                  v-if="showBreadcrumbTrail && breadcrumbs"
                  :id="breadcrumbId"
                  :breadcrumbs="breadcrumbs"
                  :home="false"
                  :separator="separator"
                />
                <CPText v-else-if="topBarTitle" size="lg" weight="semibold" class="text-body">
                  {{ topBarTitle }}
                </CPText>
              </template>
              <!-- Desktop: search only on communities *group* routes. Mobile TopBar uses useActiveSearchContext (courses, my-library, etc.) — intentional product split, not an oversight. -->
              <template #search>
                <ClientOnly>
                  <SearchWidget
                    v-if="authStore.isLoggedIn && locationId && isCommunitiesGroupRoute && isNonCPNavigationRoute"
                    :location-id="locationId"
                    :group-id="currentGroupId"
                    :placeholder="t('clientportal.search.placeholder')"
                    size="sm"
                    :use-query-params="true"
                  />
                </ClientOnly>
              </template>
            </TopBarMobile>
            <TopBarMobile
              v-else
              :show-bottom-border="true"
              class="h-full w-full"
              :show-logo="false"
              :show-auth-section="true"
              :show-search="mobileShowSearch"
              :show-chat-button="showChatButton"
              :chat-unread-count="chatUnreadCount"
              :initial-search-query="mobileInitialSearchQuery"
              @loginClick="authStore.openLoginModal()"
              @signupClick="onSignup"
              @chatClick="handleTopBarChatClick"
              @search-open="handleMobileSearchOpen"
              @search-submit="handleMobileSearchSubmit"
              @search-input-change="handleMobileSearchInputChange"
              @search-close="handleMobileSearchClose"
            >
              <template #announcements>
                <AnnouncementsTopBarButton
                  :button-id="createElementId('topbar', 'mobile', 'announcements-button')"
                  :badge-id="createElementId('topbar', 'mobile', 'announcements-unread-badge')"
                />
              </template>
              <template #left>
                <CPButton
                  :id="createElementId('sidebar', 'toggle', 'button')"
                  type="ghost"
                  size="xs"
                  class="px-1.5 touch-manipulation"
                  :aria-label="t('common.ui.openMenu')"
                  @click="toggleSidebarDrawer"
                >
                  <CustomHamburgerMenuIcon class="h-5 w-5 text-body" />
                </CPButton>
              </template>
              <template #center>
                <CPText v-if="topBarTitle" size="lg" weight="semibold" class="min-w-0 truncate text-body">
                  {{ topBarTitle }}
                </CPText>
              </template>
            </TopBarMobile>
          </header>

          <ChatContainer v-if="shouldMountChatContainer" ref="chatContainerRef" :group-slug="currentGroupSlug || undefined" />

          <main class="cp-main-content" :class="{ 'bg-background': isAuthPage }">
            <div
              class="cp-main-content-inner"
              :class="[
                isAccountSettingsPage || isCoursesRoute ? '' : isMobile ? 'p-4' : 'p-6',
                { 'cp-main-content-inner--account-settings': isAccountSettingsPage },
              ]"
            >
              <slot />
            </div>
          </main>
        </div>

        <CPPortalSidebarMobileDrawer
          v-if="showPortalMobileDrawer"
          drawer-id="cp-portal-sidebar-drawer"
          :drawer-width="280"
          :portal-name="portalName"
          :portal-icon="portalIcon"
          :navigation-groups="navigationGroupsMobile"
          :selected-item="selectedItem"
        />

        <LoginModal />
      </div>
      <!-- Anchor element for portal-level custom footer tracking code injection -->
      <div id="cp-siteCustomFooter" />
      <!-- Anchor element for communities group-level custom footer tracking code injection -->
      <div id="communities-siteCustomFooter" />
      <!-- Mount for portal-configured third-party support chat embed (postscribe); body teleport escapes shell overflow -->
      <Teleport to="body">
        <div id="clientportal-chatWidget" />
      </Teleport>
    </div>
  </HLContentWrap>
</template>

<style scoped>
  .cp-root-shell {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    min-height: 0;
    overflow: hidden;
  }

  .cp-root-container {
    --cp-sidebar-width: 16rem;
    --cp-topbar-height: 3.5rem;

    display: grid;
    grid-template-columns: var(--cp-sidebar-width) 1fr;
    /* minmax(0,1fr) + child min-h-0: grid items may shrink so sidebar nav + main scroll instead of growing past the viewport */
    grid-template-rows: minmax(0, 1fr);
    flex: 1 1 0%;
    min-height: 0;
    width: 100%;
    overflow: hidden;
    transition: grid-template-columns 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .cp-root-container > * {
    min-height: 0;
  }

  .cp-root-container.sidebar-collapsed {
    --cp-sidebar-width: 3rem;
  }

  .cp-root-container.auth-page {
    grid-template-columns: 1fr;
    transition: none;
  }

  .cp-root-container.auth-page .cp-content-area {
    grid-column: 1;
  }

  .cp-root-container.switcher-sidebar-mode .cp-main-content-inner,
  .cp-root-container.auth-page .cp-main-content-inner {
    padding: 0;
    max-width: 100%;
  }

  /*
   * Communities/courses layouts (.communities-layout) use height:100% + scroll on .col-main.
   * Default inner uses flex-shrink:0 so cp-main-content scrolls; that breaks the nested chain.
   * Constrain inner height and defer scroll to the layer layout (e.g. communities-groups.vue).
   */
  .cp-root-container.switcher-sidebar-mode .cp-main-content {
    overflow: hidden;
  }

  .cp-root-container.switcher-sidebar-mode .cp-main-content-inner {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }

  /* Account settings: TopBarMobile uses h-13 (3.25rem); match shell height so no surface gap under the bar. */
  .cp-root-container.account-settings-page {
    --cp-topbar-height: 3.25rem;
  }

  .cp-root-container.account-settings-page .cp-content-area {
    background-color: var(--cp-background-500);
  }

  .cp-root-container.account-settings-page .cp-main-content {
    overflow: hidden;
  }

  /* Account settings uses the dashboard shell. The inner account-settings.web.vue grid manages its own spacing, so zero the inner padding. */
  .cp-root-container.account-settings-page .cp-main-content-inner {
    flex: 1 1 0;
    min-height: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .cp-main-content-inner.cp-main-content-inner--account-settings {
    padding: 0;
    max-width: 100%;
  }

  .cp-root-container.sidebar-collapsed .cp-sidebar {
    width: var(--cp-sidebar-width);
  }

  .cp-content-area {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    position: relative;
    background-color: var(--cp-surface-500);
  }

  /* Mobile shell uses the page background instead of the surface tint. */
  .cp-content-area--mobile-bg {
    background-color: var(--cp-background-500);
  }

  .cp-topbar {
    flex-shrink: 0;
    height: var(--cp-topbar-height);
    width: 100%;
    z-index: 40;
  }

  /* Outer <main>: full width of the column so the vertical scrollbar sits at the viewport edge of the content area. */
  .cp-main-content {
    flex: 1;
    min-height: 0;
    overflow: auto;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
  }

  .cp-main-content-inner {
    flex: 1 0 auto;
    width: 100%;
    max-width: 1730px;
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
    display: flex;
    flex-flow: column nowrap;
    min-height: 0;
  }

  .sidebar-toggle-btn {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 30;
    width: 1rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sidebar-toggle-inner {
    position: relative;
    width: 1rem;
    height: 1rem;
  }

  .toggle-line {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 0.125rem;
    height: 0.75rem;
    background-color: var(--cp-font-700);
    border-radius: 0.03125rem;
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  .sidebar-toggle-btn:hover .toggle-line {
    opacity: 0;
  }

  .sidebar-toggle-icons {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .sidebar-toggle-btn:hover .sidebar-toggle-icons {
    opacity: 1;
  }

  .sidebar-toggle-icon {
    width: 1rem;
    height: 1rem;
    color: var(--cp-font-700);
    transition: color 0.2s ease;
  }

  .sidebar-toggle-btn:hover .sidebar-toggle-icon {
    color: var(--cp-primary-500);
  }

  .sidebar-toggle-btn:hover {
    border-left: 1px solid var(--cp-border-500);
    box-sizing: border-box;
  }

  @media (max-width: 767px) {
    .cp-root-container {
      --cp-sidebar-width: 0px;
      grid-template-columns: 1fr;
    }

    .cp-sidebar {
      display: none;
    }

    .cp-content-area {
      grid-column: 1;
    }
  }
</style>
